import { Location } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';

import { ToastService } from '@core/components/toasts/toasts.service';
import { FormBuilderService } from 'services/form-builder/form-builder.service';
import { FormSubmissionAnswerModel } from 'services/models/form-builder/form-submission-answer-model';
import { FormSubmissionModel } from 'services/models/form-builder/form-submission.model';
import { FormModel } from 'services/models/form-builder/form.model';

const defaultLoadingStates = {
  data: true,
  submission: false,
};

type FormSubmitData = {
  items: FormSubmissionAnswerModel[];
  isLastGroup: boolean;
};

@Component({
  selector: 'app-form-answer',
  templateUrl: './form-answer.component.html',
  styleUrls: ['./form-answer.component.scss'],
})
export class FormAnswersComponent implements OnInit {
  @Input() form: FormModel;
  @Input() submission: FormSubmissionModel;
  @Input() readonly = false;
  @Input() isSubmitting: boolean;
  @Input() isModalMode = false;
  @Input() currentStep: number = 0;
  @Output() onPrev = new EventEmitter();
  @Output() onCancel = new EventEmitter();
  @Output() onSubmit = new EventEmitter();

  public formAnswers: FormSubmissionAnswerModel[] = [];
  public loadingStates = { ...defaultLoadingStates };

  constructor(
    private location: Location,
    private elementRef: ElementRef<HTMLElement>,
    private deviceService: DeviceDetectorService,
    private toasterService: ToastService,
    private formBuilderService: FormBuilderService,
  ) {}

  ngOnInit(): void {}

  public onFormSubmit({ items, isLastGroup }: FormSubmitData): void {
    if (this.readonly) {
      this.handleReadonlyFlow(isLastGroup);

      return;
    }

    this.setLoadingState('submission', true);

    this.formBuilderService.saveFormSession(isLastGroup, items).subscribe({
      next: () => this.handleFormSaveSuccess(isLastGroup),
      error: () => this.setLoadingState('submission', false),
      complete: () => {
        this.setLoadingState('submission', false);
        this.scrollToTop();
      },
    });
  }

  public goBack(): void {
    this.onPrev.emit();
  }

  public goToPreviousStep(): void {
    if (this.currentStep === 0) {
      this.onCancel.emit();

      return;
    }

    this.currentStep--;
    this.scrollToTop();
  }

  private handleReadonlyFlow(isLastGroup: boolean): void {
    if (!isLastGroup) {
      this.currentStep++;

      this.scrollToTop();
    } else {
      this.onSubmit.emit({ submited: false });
    }
  }

  private handleFormSaveSuccess(isLastGroup: boolean): void {
    if (!isLastGroup) {
      this.toasterService.showSuccess('Etapa do formulário salva com sucesso.');

      this.currentStep++;
    } else {
      this.onSubmit.emit({ submited: true });

      this.toasterService.showSuccess('Formulário finalizado com sucesso.');
    }
  }

  private scrollToTop(): void {
    setTimeout(() => {
      requestAnimationFrame(() => {
        const scrollTarget = this.elementRef.nativeElement.querySelector(
          '.form-builder-answer-component .card-body'
        ) as HTMLElement | null;

        this.runScrollToTop(scrollTarget);
      });
    });
  }
  
  private runScrollToTop(scrollTarget: HTMLElement): void {
    if (!scrollTarget) return;
    
    if (!this.deviceService.isMobile()) {
      scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      return;
    }
    
    const top = scrollTarget.getBoundingClientRect().top + window.pageYOffset - this.getScrollOffset();
    
    window.scrollTo({ top: top - 180, behavior: 'smooth' });
  }

  private getScrollOffset(): number {
    const fixedHeader = document.querySelector('.header-navbar.fixed-top') as HTMLElement | null;
    
    const headerHeight = fixedHeader?.offsetHeight ?? 0;

    return headerHeight + 16;
  }

  private setLoadingState(key: keyof typeof defaultLoadingStates, state: boolean): void {
    this.loadingStates[key] = state;
  }
}
